import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { useApi } from "../../lib/useApi";
import { api, formatApiError } from "../../lib/api";
import { Loader, ErrorState, PageHeader } from "../../components/common/States";
import { TYPE, fmtDate } from "../../lib/labels";
import { usePlatformCan } from "../../lib/platform";
import PlatformAnalytics from "../../components/admin/PlatformAnalytics";
import AdminTrends from "../../components/admin/AdminTrends";

const Kpi = ({ label, value, hint, testId }) => (
  <div className="card-surface p-5" data-testid={testId}>
    <p className="eyebrow">{label}</p>
    <p className="mt-3 font-display text-3xl font-bold">{value}</p>
    {hint && <p className="mt-1 text-xs text-slate-500">{hint}</p>}
  </div>
);

const Pending = ({ data, reload }) => {
  const can = usePlatformCan();
  const activate = async (id) => {
    try { await api.patch(`/admin/companies/${id}`, { status: "active" }); toast.success("Entreprise approuvée"); reload(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="card-surface p-5 lg:col-span-2" data-testid="admin-pending-list">
      <div className="flex items-center justify-between">
        <p className="font-display font-semibold">Demandes en attente de validation</p>
        <Link to="/admin/entreprises" className="text-sm font-medium text-indigo-700" data-testid="admin-see-companies-link">Toutes les entreprises</Link>
      </div>
      {!data.pending.length && <p className="mt-4 text-sm text-slate-500">Aucune demande en attente.</p>}
      <div className="mt-3 divide-y">
        {data.pending.map((p) => (
          <div key={p.id} className="flex flex-wrap items-center gap-3 py-3">
            <div className="flex-1"><p className="font-medium">{p.name}</p><p className="text-xs text-slate-500">{p.sector} · {p.city}, {p.country} · {fmtDate(p.created_at)}</p></div>
            {can("companies:approve") && <Button size="sm" onClick={() => activate(p.id)} data-testid={`admin-activate-${p.id}`}>Approuver</Button>}
          </div>
        ))}
      </div>
    </div>
  );
};

export default function AdminDashboard() {
  const { data, loading, error, reload } = useApi("/admin/stats");
  if (loading) return <Loader />;
  if (error) return <ErrorState message={error} onRetry={reload} />;
  const c = data.companies;
  const u = data.users_by_type;
  const billingOff = !data.modules.billing ? "module facturation non activé" : null;
  return (
    <div>
      <PageHeader eyebrow="Plateforme" title="Vue globale" description="Indicateurs réels de l'ensemble des espaces VOXA AFRIKA." />
      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-5">
        <Kpi testId="admin-kpi-registered" label="Entreprises inscrites" value={data.companies_total} hint={`${c.pending || 0} en attente · ${c.suspended || 0} suspendue(s)`} />
        <Kpi testId="admin-kpi-active" label="Entreprises actives" value={c.active || 0} />
        <Kpi testId="admin-kpi-users" label="Utilisateurs" value={data.users} hint={`${u.public} usagers · ${u.collaborators} collaborateurs · ${u.internal} internes`} />
        <Kpi testId="admin-kpi-agencies" label="Agences" value={data.agencies} />
        <Kpi testId="admin-kpi-services" label="Services" value={data.services} />
        <Kpi testId="admin-kpi-feedbacks" label="Feedbacks" value={data.feedbacks} />
        <Kpi testId="admin-kpi-subscriptions" label="Abonnements" value={data.subscriptions.total} hint={Object.entries(data.subscriptions.by_status).map(([k, n]) => `${n} ${k === "trial" ? "en essai" : k}`).join(" · ") || "aucun"} />
        <Kpi testId="admin-kpi-invoices" label="Factures" value={data.invoices.total} hint={billingOff} />
        <Kpi testId="admin-kpi-unpaid" label="Impayés" value={data.invoices.unpaid} hint={billingOff} />
        <Kpi testId="admin-kpi-tickets" label="Tickets ouverts" value={data.tickets.open} hint={!data.modules.tickets ? "module support non activé" : `${data.tickets.total} au total`} />
      </div>
      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        <Pending data={data} reload={reload} />
        <div className="space-y-6">
          <div className="card-surface p-5">
            <p className="font-display font-semibold">Feedbacks par type</p>
            <div className="mt-3 space-y-2 text-sm">{Object.entries(TYPE).map(([k, t]) => <div key={k} className="flex justify-between"><span>{t.label}</span><b>{data.feedbacks_by_type[k] || 0}</b></div>)}</div>
          </div>
          <div className="card-surface p-5" data-testid="admin-countries">
            <p className="font-display font-semibold">Entreprises par pays</p>
            <div className="mt-3 space-y-2 text-sm">{data.countries.map((r) => <div key={r.country} className="flex justify-between"><span>{r.country}</span><b>{r.count}</b></div>)}</div>
          </div>
        </div>
      </div>
      <AdminTrends />
      <PlatformAnalytics />
    </div>
  );
}
